import {
  CognitoIdentityProviderClient,
  ConfirmSignUpCommand,
  ResendConfirmationCodeCommand,
  SignUpCommand,
} from "@aws-sdk/client-cognito-identity-provider";

const region = import.meta.env.VITE_AWS_REGION || "us-east-2";
const clientId = import.meta.env.VITE_COGNITO_USER_POOL_CLIENT_ID || "2cecedqc85vsjcs29i37qf3iii";

const cognito = new CognitoIdentityProviderClient({ region });

export async function signUpWithCognito(email: string, password: string, name?: string) {
  const username = normalizeEmail(email);
  const attributes = [{ Name: "email", Value: username }];
  if (name?.trim()) attributes.push({ Name: "name", Value: name.trim() });

  try {
    await cognito.send(
      new SignUpCommand({
        ClientId: clientId,
        Username: username,
        Password: password,
        UserAttributes: attributes,
      }),
    );
  } catch (error) {
    if (cognitoErrorName(error) !== "UsernameExistsException") throw friendlyCognitoError(error);
    try {
      await resendCognitoConfirmationCode(username);
    } catch (resendError) {
      throw friendlyCognitoError(resendError);
    }
  }
}

export async function confirmCognitoSignUp(email: string, code: string) {
  try {
    await cognito.send(
      new ConfirmSignUpCommand({
        ClientId: clientId,
        Username: normalizeEmail(email),
        ConfirmationCode: code.trim(),
      }),
    );
  } catch (error) {
    throw friendlyCognitoError(error);
  }
}

export async function resendCognitoConfirmationCode(email: string) {
  try {
    await cognito.send(
      new ResendConfirmationCodeCommand({
        ClientId: clientId,
        Username: normalizeEmail(email),
      }),
    );
  } catch (error) {
    throw friendlyCognitoError(error);
  }
}

function normalizeEmail(email: string) {
  return email.trim().toLowerCase();
}

function cognitoErrorName(error: unknown) {
  return typeof error === "object" && error && "name" in error ? String(error.name) : undefined;
}

function friendlyCognitoError(error: unknown) {
  const name = cognitoErrorName(error);
  if (name === "CodeMismatchException") return new Error("That verification code is not correct.");
  if (name === "ExpiredCodeException") return new Error("That verification code expired. Request a new code.");
  if (name === "LimitExceededException") return new Error("Too many attempts. Wait a few minutes, then try again.");
  if (name === "InvalidPasswordException") return new Error("Password must be at least 12 characters and include uppercase, lowercase, numbers, and symbols.");
  if (name === "InvalidParameterException") {
    return new Error("This account may already be confirmed. Try signing in.");
  }
  if (name === "TooManyRequestsException") return new Error("Too many requests. Wait a few minutes, then try again.");
  if (error instanceof Error) return error;
  return new Error("Account verification failed.");
}
